import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { currencyService, Currency } from '@/services/currency.service';
import { useAuthStore } from '@/store/authStore';
import {
  AED_DISPLAY_SIGN_CHAR,
  formatWithCurrencySymbol,
  resolveAedDisplaySymbol,
} from '@/constants/uaDirhamDisplay';
import { convertToBaseCurrency as convertToBaseCurrencyUtil } from '@/utils/currencyConversion.util';

interface CurrencyContextType {
  baseCurrency: Currency | null;
  allCurrencies: Currency[];
  loading: boolean;
  refreshCurrencies: () => Promise<void>;
  formatCurrency: (amount: number, currencyCode?: string) => string;
  convertToBaseCurrency: (amount: number, currencyCode?: string) => number;
  formatBaseCurrency: (amount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [baseCurrency, setBaseCurrency] = useState<Currency | null>(null);
  const [allCurrencies, setAllCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuthStore();

  const loadCurrencies = async () => {
    try {
      setLoading(true);
      console.log('[CurrencyContext] Loading currencies...');
      const currencies = await currencyService.getCurrencies();
      console.log('[CurrencyContext] Loaded currencies:', currencies.length);
      setAllCurrencies(currencies);
      
      // Find base currency
      const base = currencies.find((c) => c.is_base_currency);
      if (base) {
        console.log('[CurrencyContext] Found base currency:', base.currency_code);
        setBaseCurrency(base);
      } else if (currencies.length > 0) {
        // Use first currency as fallback if no base currency found
        console.log('[CurrencyContext] No base currency found, using first currency:', currencies[0].currency_code);
        setBaseCurrency(currencies[0]);
      } else {
        // Set default fallback if no currencies at all
        console.warn('[CurrencyContext] No currencies found, using default AED');
        setBaseCurrency({
          id: '',
          company_id: '',
          currency_code: 'AED',
          currency_name: 'UAE Dirham',
          symbol: AED_DISPLAY_SIGN_CHAR,
          is_base_currency: true,
          exchange_rate: 1,
          is_active: true,
          created_at: '',
          updated_at: '',
        });
      }
    } catch (error: any) {
      console.error('[CurrencyContext] Error loading currencies:', error);
      console.error('[CurrencyContext] Error details:', error.response?.data);
      // Set default fallback
      setBaseCurrency({
        id: '',
        company_id: '',
        currency_code: 'AED',
        currency_name: 'UAE Dirham',
        symbol: AED_DISPLAY_SIGN_CHAR,
        is_base_currency: true,
        exchange_rate: 1,
        is_active: true,
        created_at: '',
        updated_at: '',
      });
      setAllCurrencies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only load currencies if user is authenticated
    if (isAuthenticated) {
      loadCurrencies();
    } else {
      // Set default fallback immediately if not authenticated
      setBaseCurrency({
        id: '',
        company_id: '',
        currency_code: 'AED',
        currency_name: 'UAE Dirham',
        symbol: AED_DISPLAY_SIGN_CHAR,
        is_base_currency: true,
        exchange_rate: 1,
        is_active: true,
        created_at: '',
        updated_at: '',
      });
      setLoading(false);
    }
  }, [isAuthenticated]);

  const formatCurrency = (amount: number, currencyCode?: string): string => {
    if (currencyCode) {
      const matched = allCurrencies.find((c) => c.currency_code === currencyCode);
      if (matched) {
        const code = matched.currency_code;
        const rawSym = matched.symbol || '';
        const sym =
          String(code).toUpperCase() === 'AED' ? resolveAedDisplaySymbol(rawSym) : rawSym;
        return formatWithCurrencySymbol(amount, sym, code);
      }
      // Document currency not in master list — still show the ISO code, not base currency.
      return formatWithCurrencySymbol(amount, currencyCode, currencyCode);
    }

    const currency = baseCurrency;

    if (!currency) {
      return formatWithCurrencySymbol(amount, AED_DISPLAY_SIGN_CHAR, 'AED');
    }
    const code = currency.currency_code;
    const rawSym = currency.symbol || '';
    const sym =
      String(code).toUpperCase() === 'AED' ? resolveAedDisplaySymbol(rawSym) : rawSym;
    return formatWithCurrencySymbol(amount, sym, code);
  };

  const convertToBaseCurrency = useCallback(
    (amount: number, currencyCode?: string): number => {
      return convertToBaseCurrencyUtil(
        amount,
        currencyCode,
        allCurrencies,
        baseCurrency?.currency_code
      );
    },
    [allCurrencies, baseCurrency?.currency_code]
  );

  const formatBaseCurrency = useCallback(
    (amount: number): string => {
      return formatCurrency(amount, baseCurrency?.currency_code);
    },
    [baseCurrency?.currency_code, allCurrencies]
  );

  return (
    <CurrencyContext.Provider
      value={{
        baseCurrency,
        allCurrencies,
        loading,
        refreshCurrencies: loadCurrencies,
        formatCurrency,
        convertToBaseCurrency,
        formatBaseCurrency,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

